import { config, isUpdateRequired, featureFlags } from './config';

// Store the force update state in localStorage to persist across sessions
const FORCE_UPDATE_KEY = 'holifya_force_update_required';
const VERSION_CHECK_INTERVAL = 60 * 1000; // Check every 60 seconds (1 minute) for testing
const VERSION_ENDPOINT = '/version.json'; // Endpoint to fetch server version

interface ServerVersionInfo {
  version: string;
  minimumRequiredVersion: string;
  buildTime: string;
  forceUpdate: boolean;
}

class ForceUpdateDetector {
  private static instance: ForceUpdateDetector;
  private checkInterval: NodeJS.Timeout | null = null;
  private modalShown = false;

  private constructor() {
    this.initialize();
  }

  public static getInstance(): ForceUpdateDetector {
    if (!ForceUpdateDetector.instance) {
      ForceUpdateDetector.instance = new ForceUpdateDetector();
    }
    return ForceUpdateDetector.instance;
  }

  private initialize() {
    // Respect feature flags to disable force update detection entirely
    // Requires BOTH general update detection and force update to be enabled
    if (!featureFlags.enableUpdateDetection || !featureFlags.enableForceUpdate) {
      return;
    }

    // Skip force update detection in development
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    // Check immediately on initialization
    this.checkForForceUpdate();
    
    // Set up periodic checking
    this.startPeriodicCheck();
    
    // Listen for storage changes (in case another tab updates the version)
    window.addEventListener('storage', this.handleStorageChange.bind(this));
    
    // Listen for focus events (when user comes back to the tab)
    window.addEventListener('focus', this.handleFocus.bind(this));
  }

  private async checkForForceUpdate() {
    try {
      // If force update is disabled, skip any checks
      if (!featureFlags.enableForceUpdate) {
        return;
      }

      // Skip force update checks in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Force update check skipped in development mode');
        return;
      }

      // Get current client version
      const clientVersion = (window as any).APP_VERSION || config.version;
      
      // Fetch server version info
      const serverVersionInfo = await this.fetchServerVersion();
      
      if (!serverVersionInfo) {
        console.warn('Could not fetch server version, using local config');
        // Fallback to local config check
        if (isUpdateRequired(clientVersion)) {
          this.showForceUpdateModal(clientVersion, config.minimumRequiredVersion);
        }
        return;
      }
      
      // Compare client version with server's minimum required version
      if (isUpdateRequired(clientVersion, serverVersionInfo.minimumRequiredVersion)) {
        this.showForceUpdateModal(clientVersion, serverVersionInfo.minimumRequiredVersion);
        return;
      }
      
      // If no update required, clear any stored force update flag
      localStorage.removeItem(FORCE_UPDATE_KEY);
      
    } catch (error) {
      console.warn('Error checking for force update:', error);
    }
  }

  private async fetchServerVersion(): Promise<ServerVersionInfo | null> {
    try {
      // Add cache-busting parameter to prevent caching issues
      const timestamp = Date.now();
      const response = await fetch(`${VERSION_ENDPOINT}?t=${timestamp}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      return {
        version: data.version,
        minimumRequiredVersion: data.minimumRequiredVersion || data.version,
        buildTime: data.buildTime,
        forceUpdate: data.forceUpdate || false
      };
    } catch (error) {
      console.warn('Failed to fetch server version:', error);
      return null;
    }
  }

  private showForceUpdateModal(clientVersion: string, requiredVersion: string) {
    // If force update is disabled, never show the modal
    if (!featureFlags.enableForceUpdate) return;

    // Prevent showing multiple modals
    if (this.modalShown) return;
    
    // Check if modal already exists
    if (document.getElementById('force-update-modal')) return;
    
    this.modalShown = true;
    localStorage.setItem(FORCE_UPDATE_KEY, 'true');
    
    // Create and show the force update modal
    const modal = document.createElement('div');
    modal.id = 'force-update-modal';
    modal.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 99999;
        font-family: system-ui, -apple-system, sans-serif;
      ">
        <div style="
          background: white;
          border-radius: 12px;
          padding: 24px;
          max-width: 400px;
          margin: 20px;
          text-align: center;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        ">
          <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
          <h2 style="
            font-size: 24px;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 12px;
          ">Aggiornamento Obbligatorio</h2>
          <p style="
            color: #6b7280;
            margin-bottom: 20px;
            line-height: 1.5;
          ">${config.forceUpdateMessage}</p>
          
          <div style="
            background: #f3f4f6;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 20px;
            font-size: 14px;
          ">
            <p style="margin: 4px 0; color: #374151;">
              <strong>Versione attuale:</strong> ${clientVersion}
            </p>
            <p style="margin: 4px 0; color: #374151;">
              <strong>Versione minima richiesta:</strong> ${requiredVersion}
            </p>
          </div>
          
          <button onclick="clearCacheAndReload()" style="
            background: #2563eb;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 500;
            cursor: pointer;
            font-size: 16px;
            margin-bottom: 12px;
            width: 100%;
          ">Aggiorna Ora</button>
          
          <button onclick="alert('L\\'aggiornamento è obbligatorio per continuare a utilizzare l\\'app.')" style="
            background: transparent;
            color: #6b7280;
            border: none;
            padding: 8px;
            cursor: pointer;
            font-size: 14px;
            text-decoration: underline;
          ">Perché devo aggiornare?</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    // Prevent any interaction with the page behind the modal
    document.body.style.overflow = 'hidden';
  }

  private startPeriodicCheck() {
    this.checkInterval = setInterval(() => {
      this.checkForForceUpdate();
    }, VERSION_CHECK_INTERVAL);
  }

  private handleStorageChange(event: StorageEvent) {
    if (event.key === FORCE_UPDATE_KEY && event.newValue === 'true') {
      this.checkForForceUpdate();
    }
  }

  private handleFocus() {
    // Check for updates when user returns to the tab
    this.checkForForceUpdate();
  }

  public stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    
    window.removeEventListener('storage', this.handleStorageChange.bind(this));
    window.removeEventListener('focus', this.handleFocus.bind(this));
  }
}

// Initialize the force update detector
export const forceUpdateDetector = ForceUpdateDetector.getInstance();

// Export a function to manually check for updates
export const checkForForceUpdate = () => {
  forceUpdateDetector['checkForForceUpdate']();
};

// Export a function to clear cache and reload
export const clearCacheAndReload = () => {
  // Clear all caches
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        caches.delete(name);
      });
    });
  }
  
  // Clear localStorage
  localStorage.clear();
  
  // Force reload
  window.location.reload();
};

// Export a function to manually trigger update check with cache busting
export const forceUpdateCheck = () => {
  // Clear any stored force update flag
  localStorage.removeItem(FORCE_UPDATE_KEY);
  
  // Reset modal state
  forceUpdateDetector['modalShown'] = false;
  
  // Check for updates
  forceUpdateDetector['checkForForceUpdate']();
}; 